import { validator } from 'lgtm';
import { Country } from '@square/dashboard-protos/models/squareup/common/countries';

// test for six alphanumeric characters (does not expect space)
export const POSTAL_CODE_VALIDATOR_CA = postalCode => /^\w{6}$/.test(postalCode);
// test for five or nine numeric characters (does not expect hyphenation)
export const POSTAL_CODE_VALIDATOR_US = postalCode => /^(?:\d{5}|\d{9})$/.test(postalCode);
export const POSTAL_CODE_VALIDATOR_GB = postalCode => /^(([a-z]{1,2}\d[\da-z]?|ascn|sthl|tdcu|bbnd|[bfs]iqq|pcrn|tkca) ?\d[a-z]{2}|bfpo ?\d{1,4}|(ky\d|msr|vg|ai)[ -]?\d{4}|[a-z]{2} ?\d{2}|ge ?cx|gir ?0a{2}|san ?ta1)$/i.test(postalCode);
export const postalCodeValidatorForCountry = country => {
  switch (country) {
    case Country.US:
      return POSTAL_CODE_VALIDATOR_US;
    case Country.CA:
      return POSTAL_CODE_VALIDATOR_CA;
    case Country.GB:
      return POSTAL_CODE_VALIDATOR_GB;
    default:
      throw new Error(`No postal code validator exists for Country (countryCode: ${country})`);
  }
};

/**
 * Checks if an address line is valid (i.e., not a PO Box).
 *
 * Disallows standard PO Box formats like "P.O. Box 123", "PO Box", "Post Office Box", "Box 456", etc.
 * Allows valid military/diplomatic formats like "PSC 123 Box 456" or "FPO 1 Box 789", which are not considered PO Boxes.
 *
 * Logic mirrors the Java implementation in Square's PoBoxChecker:
 * https://github.com/squareup/java/blob/master/common/pobox/src/main/java/com/squareup/common/pobox/PoBoxChecker.java
 */
export const addressLineValid = value => {
  const cleaned = value.replace(/[^\d A-Za-z]/g, '');
  const isPoBox = new RegExp([
  // Matches: "PO Box", "Post Office Box", only when "po" or "post office" are full words
  '\\b(?:post(?:al)?\\s*office|po)\\b\\s*(?:box|number)?(?:\\s*\\d+)?',
  // Matches: "P O Box", "P.O Box", optionally with digits
  '\\bp\\s*\\.?\\s*[o0]\\s*\\.?\\s*box(?:\\s*\\d+)?'].join('|'), 'i').test(cleaned);

  // Allows valid military/diplomatic formats like "PSC 123 Box 456"
  const isAllowedFormat = /[a-z]+\s+\d+\s+box\s+\d+/i.test(cleaned);
  return !isPoBox || isAllowedFormat;
};
const globalAddressValidatorGB = (country, t) => validator().validates('addressLine1').required(t('banking.checking.onboarding.address.validation.addressLine1')).validates('locality').required(t('banking.checking.onboarding.address.validation.locality')).validates('postalCode').using(postalCodeValidatorForCountry(country), t('banking.checking.onboarding.address.validation.postalCode.postal')).build();
const globalAddressValidatorUSCA = (country, t, disallowPoBoxesInAddressLines = false) => {
  const v = validator().validates('addressLine1').required(t('banking.checking.onboarding.address.validation.addressLine1')).validates('addressLine2').using(() => true, ''); // No-op rule to ensure addressLine2 is included in the validation output for tests

  if (disallowPoBoxesInAddressLines) {
    v.validates('addressLine1').using(addressLineValid, t('banking.checking.onboarding.address.validation.noPoBox'));
    v.validates('addressLine2').using(addressLineValid, t('banking.checking.onboarding.address.validation.noPoBox'));
  }
  return v.validates('locality').required(t('banking.checking.onboarding.address.validation.locality')).validates('administrativeDistrictLevel1').required(t('banking.checking.onboarding.address.validation.administrativeDistrictLevel1')).validates('postalCode').using(postalCodeValidatorForCountry(country), country === Country.US ? t('banking.checking.onboarding.address.validation.postalCode.zip') : t('banking.checking.onboarding.address.validation.postalCode.postal')).build();
};
const globalAddressValidator = (country, t, disallowPoBoxesInAddressLines = false) => {
  switch (country) {
    case Country.US:
    case Country.CA:
      return globalAddressValidatorUSCA(country, t, disallowPoBoxesInAddressLines);
    case Country.GB:
      return globalAddressValidatorGB(country, t);
    default:
      throw new Error(`No address validator for Country (countryCode: ${country})`);
  }
};
export default globalAddressValidator;